import React from "react";
import { X, BookOpen, FileText, ListChecks, Layers, Trash2, Info } from "lucide-react";

/**
 * Modal explaining how PMs add and manage their own practice-specific
 * courses inside the Admin dashboard. Opens from the "How does this work?"
 * button in the Course Manager header. No API calls — purely instructional.
 */
export default function CourseManagerHelpModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8 bg-brand-ink/40 backdrop-blur-sm"
      data-testid="course-manager-help-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl bg-white border border-brand-linen rounded-3xl shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 p-6 border-b border-brand-linen">
          <BookOpen className="w-6 h-6 text-brand-accent shrink-0 mt-0.5" strokeWidth={1.6} />
          <div className="flex-1 min-w-0">
            <h2 className="font-serif text-2xl text-brand-ink">Adding your own courses</h2>
            <p className="text-sm text-brand-muted mt-1">
              A quick walk-through for Practice Managers. Everything you create here is scoped
              to your practice and invisible to any other Calside practice.
            </p>
          </div>
          <button
            type="button"
            data-testid="course-manager-help-close"
            onClick={onClose}
            className="ml-auto rounded-full p-2 hover:bg-brand-linen text-brand-muted hover:text-brand-ink"
            aria-label="Close"
          >
            <X className="w-5 h-5" strokeWidth={1.7} />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          <Step
            n={1}
            icon={<FileText className="w-5 h-5" strokeWidth={1.6} />}
            title="Create the course"
          >
            <p>
              Click <strong>+ New course</strong> at the top of this section. Fill in:
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>Title</strong> — e.g. <em>&ldquo;Braidcraft — Fire evacuation drill 2026&rdquo;</em></li>
              <li><strong>Subtitle</strong> <em>(optional)</em> — one-line summary shown on the dashboard tile</li>
              <li><strong>Category</strong> — how the course is grouped on the learner Dashboard</li>
              <li><strong>Description</strong> — the &ldquo;about this course&rdquo; block learners see before they start</li>
              <li><strong>Refresh months</strong> <em>(optional)</em> — how often the certificate should be re-earned. 12 = annual · 36 = 3-yearly · blank = one-off</li>
            </ul>
            <p className="mt-2">Click <strong>Save</strong> — the course is instantly live for your learners.</p>
          </Step>

          <Step
            n={2}
            icon={<Layers className="w-5 h-5" strokeWidth={1.6} />}
            title="Add lessons"
          >
            <p>
              Open the course → <strong>Lessons</strong> tab → <strong>+ New lesson</strong>. Each lesson has a title,
              optional summary, and a content body.
            </p>
            <p className="mt-2">The content body supports <strong>Markdown</strong>:</p>
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li><code className="bg-brand-bone px-1 rounded">**bold**</code> for key terms</li>
              <li><code className="bg-brand-bone px-1 rounded">*italic*</code> for emphasis</li>
              <li><code className="bg-brand-bone px-1 rounded"># Heading</code> / <code className="bg-brand-bone px-1 rounded">## Sub-heading</code></li>
              <li>Bullet lists with <code className="bg-brand-bone px-1 rounded">-</code> or numbered lists with <code className="bg-brand-bone px-1 rounded">1.</code></li>
              <li>Line breaks — just press Enter</li>
            </ul>
            <p className="mt-2 text-brand-muted italic">
              Lesson order = save order. The first lesson you save becomes Lesson 1, the next becomes
              Lesson 2, and so on.
            </p>
          </Step>

          <Step
            n={3}
            icon={<ListChecks className="w-5 h-5" strokeWidth={1.6} />}
            title="Add a quiz (recommended)"
          >
            <p>
              <strong>Quiz</strong> tab → <strong>+ Add question</strong>. For each question:
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>Question text</strong></li>
              <li><strong>4 options</strong> (A, B, C, D)</li>
              <li><strong>Correct answer</strong> — tick the right one</li>
              <li><strong>Explanation</strong> — shown to the learner after they answer, so it teaches, not just tests</li>
            </ul>
            <p className="mt-2">
              Set the <strong>Pass score</strong> at the top of the quiz (default 80%). Lower it to 70%
              for dry topics, raise it to 90% for safety-critical ones. Learners can only earn a
              certificate if they pass.
            </p>
          </Step>

          <Step
            n={4}
            icon={<Layers className="w-5 h-5 rotate-45" strokeWidth={1.6} />}
            title="Add flashcards (optional)"
          >
            <p>
              <strong>Flashcards</strong> tab → <strong>+ Add flashcard</strong>. Each has a <strong>Front</strong> (prompt) and
              a <strong>Back</strong> (answer). These feed the spaced-repetition reviewer on the learner
              Dashboard.
            </p>
            <p className="mt-2 italic text-brand-muted">
              Great for phone numbers, extension codes, corridor codes, drug names — anything you want
              the team to recall cold.
            </p>
          </Step>

          <Step
            n={5}
            icon={<Info className="w-5 h-5" strokeWidth={1.6} />}
            title="Editing later"
          >
            <p>
              Click any course in <em>Your practice&apos;s courses</em> → same editor opens. Changes save
              instantly and <strong>learner progress is preserved</strong>. There&apos;s no separate publish step.
            </p>
          </Step>

          <Step
            n={6}
            icon={<Trash2 className="w-5 h-5 text-red-600" strokeWidth={1.6} />}
            title="Deleting a course"
            danger
          >
            <p>
              Course editor → <strong>Delete course</strong> (red button, bottom-left).
              <span className="block mt-1 text-red-700 font-medium">
                Warning: this removes all lessons, the quiz, all learner progress and any
                certificates already earned. Usually better to edit than delete.
              </span>
            </p>
          </Step>

          <div className="rounded-2xl border border-brand-linen bg-brand-bone/60 p-4 text-sm text-brand-body">
            <div className="text-[11px] uppercase tracking-widest text-brand-muted mb-1">Calside library</div>
            <p>
              These sit in the right-hand list below and are <strong>read-only</strong> — you can&apos;t edit them.
              But you can copy a lesson&apos;s text and paste it into one of your own courses if you want a
              local variant (e.g. add practice-specific fire assembly points to a copy of the Fire Safety
              course).
            </p>
          </div>
        </div>

        <div className="p-4 border-t border-brand-linen flex justify-end">
          <button
            type="button"
            data-testid="course-manager-help-got-it"
            onClick={onClose}
            className="bg-brand-ink text-brand-bone rounded-full px-5 py-2 hover:bg-brand-inkHover text-sm"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

function Step({ n, icon, title, children, danger }) {
  return (
    <div className="flex gap-4">
      <div
        className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium ${
          danger ? "bg-red-100 text-red-700" : "bg-brand-linen text-brand-ink"
        }`}
      >
        {n}
      </div>
      <div className="flex-1 min-w-0 text-sm text-brand-body leading-relaxed">
        <div className="flex items-center gap-2 mb-1">
          <span className={danger ? "text-red-700" : "text-brand-accent"}>{icon}</span>
          <h3 className="font-serif text-lg text-brand-ink">{title}</h3>
        </div>
        {children}
      </div>
    </div>
  );
}
